import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { legendClasses } from '@mui/x-charts/ChartsLegend';
import { DashboardCard, LineChart, Stack, Statistic } from '@monorepo/nova-ui';
import { useDashboard } from '../../../../../contexts';
import { ChartGradient } from '../../../performance-chart/components';
import { GRADIENT_ID } from '../../../performance-chart/constants';

export const UnrealizedProfitCard: FC = () => {
  const { accountBalance } = useDashboard();

  const { t } = useTranslation('keyMetrics');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.between('xs', 'md'));

  const unrealizedProfit = accountBalance?.unrealized ?? 0;

  return (
    <DashboardCard
      sx={{
        justifyContent: 'space-between',
        position: 'relative',
        overflow: 'hidden',
        minHeight: 100,
      }}
    >
      <Typography
        variant="body2"
        sx={{
          color: theme.palette.text.second,
          fontSize: theme.typography.pxToRem(14),
        }}
      >
        {t('cards.unrealizedProfit.title')}
      </Typography>
      <Stack
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
        sx={{
          width: '100%',
        }}
      >
        <Typography
          component="div"
          sx={(theme) => ({
            fontSize: theme.typography.pxToRem(24),
          })}
        >
          <Statistic
            value={Math.abs(unrealizedProfit).toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
            prefix={unrealizedProfit < 0 ? '-$' : '$'}
          />
        </Typography>
        <Box
          sx={{
            width: {
              xs: 240,
              md: 200,
              lg: 200,
            },
            height: {
              xs: 92,
              md: 120,
              lg: 160,
            },
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            position: 'absolute',
            right: {
              xs: -20,
              md: 0,
              lg: 0,
            },
            top: {
              xs: 20,
              md: 0,
              lg: 0,
            },
            pointerEvents: 'none',
            overflow: 'hidden',
          }}
        >
          <LineChart
            width={200}
            height={isMobile ? 80 : 160}
            margin={{ top: -16, bottom: -16, left: 0, right: -16 }}
            series={[
              {
                data: [24, 26, 22, 30, 28, 32, 31],
                id: 'unrealized',
                area: true,
                showMark: false,
                color: theme.palette.primary.main,
              },
            ]}
            xAxis={[
              {
                scaleType: 'point',
                data: ['1', '2', '3', '4', '5', '6', '7'],
                disableLine: true,
                disableTicks: true,
                tickLabelInterval: () => false,
              },
            ]}
            yAxis={[
              {
                min: 20,
                max: 35,
                disableLine: true,
                disableTicks: true,
                tickLabelInterval: () => false,
              },
            ]}
            sx={{
              width: 120,
              [`& .MuiAreaElement-series-unrealized`]: {
                fill: `url(#${GRADIENT_ID})`,
              },
              [`& .MuiLineElement-series-unrealized`]: {
                strokeWidth: 2,
              },
              [`& .${legendClasses.root}`]: { display: 'none' },
            }}
          >
            <ChartGradient />
          </LineChart>
        </Box>
      </Stack>
    </DashboardCard>
  );
};
